import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import moment from "moment";
import "moment/locale/es";
import React from "react";
import { FormattedMessage } from "react-intl";
import { QueryStatus } from "react-query";
import styled from "styled-components";
import cuideoTheme from "../../../../containers/themes/defaultTheme";

const StyledTable = styled(Table)`
  .MuiTableCell-body {
    border: none;
  }
`;

const StyledBodyRow = styled(TableRow)`
  background-color: #fff;
`;

const StyledHeadCell = styled(TableCell)`
  background-color: rgba(0, 72, 120, 0.1);
  color: ${cuideoTheme.palette.primary.light};
  font-size: 0.75rem;
  line-height: normal;
  font-weight: 700;
`;

const StyledBodyCell = styled(TableCell)`
  font-size: 0.875rem;
  color: ${cuideoTheme.palette.primary.main};
  line-height: 1.25;
`;

const StyledReasonCell = styled(StyledBodyCell)`
  white-space: pre-line;
`;

const StyledLoadingWrapper = styled(TableCell)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 4rem;
  width: 100%;
`;

const StyledErrorWrapper = styled(TableCell)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 4rem;
`;

const StyledEmptyText = styled(Typography)`
  font-size: 0.875rem;
  text-align: center;
`;

const COLUMN_COUNT = 3;

export type TDiscardedValuationRow = {
  id: string;
  contratoName?: string;
  statusUpdatedAt: string;
  reason: string;
};

interface IDiscardedValuationsTableProps {
  status: QueryStatus;
  rows?: TDiscardedValuationRow[];
  emptyMessage: React.ReactNode;
}

/**
 * ECO-1293: the listing behind every "Descartados por ..." sub-tab of the
 * candidate profile. The sub-tabs only differ in where the rows come from and
 * in what to say when there are none.
 */
const DiscardedValuationsTable = (props: IDiscardedValuationsTableProps) => {
  const { status, rows, emptyMessage } = props;

  return (
    <StyledTable>
      <TableHead>
        <TableRow>
          <StyledHeadCell>
            <FormattedMessage
              id="DiscardedValuationsTable.Contract"
              defaultMessage="Contrato"
            />
          </StyledHeadCell>
          <StyledHeadCell>
            <FormattedMessage
              id="DiscardedValuationsTable.Date"
              defaultMessage="Fecha"
            />
          </StyledHeadCell>
          <StyledHeadCell>
            <FormattedMessage
              id="DiscardedValuationsTable.DiscardReason"
              defaultMessage="Motivo del descarte"
            />
          </StyledHeadCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {status === "loading" && (
          <StyledBodyRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <StyledLoadingWrapper>
                <CircularProgress thickness={5} size={22} />
              </StyledLoadingWrapper>
            </TableCell>
          </StyledBodyRow>
        )}
        {status === "error" && (
          <StyledBodyRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <StyledErrorWrapper>
                <Alert severity="error">
                  <FormattedMessage
                    id="DiscardedValuationsTable.Ha habido un error"
                    defaultMessage="Ha habido un error"
                  />
                </Alert>
              </StyledErrorWrapper>
            </TableCell>
          </StyledBodyRow>
        )}
        {status === "success" && rows && (
          <>
            {rows.length === 0 ? (
              <StyledBodyRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <StyledEmptyText>{emptyMessage}</StyledEmptyText>
                </TableCell>
              </StyledBodyRow>
            ) : (
              rows.map((row) => (
                <StyledBodyRow key={row.id}>
                  <StyledBodyCell>{row.contratoName}</StyledBodyCell>
                  <StyledBodyCell>
                    {moment(row.statusUpdatedAt).format("DD/MM/YYYY HH:mm")}
                  </StyledBodyCell>
                  <StyledReasonCell>{row.reason}</StyledReasonCell>
                </StyledBodyRow>
              ))
            )}
          </>
        )}
      </TableBody>
    </StyledTable>
  );
};

export default DiscardedValuationsTable;
